/**
 * @fileoverview AdminSettings — Portal preferences and configuration.
 *
 * Sections:
 *   1. Project Preferences (live) — default view, default sort, sort direction.
 *      Reads/writes to Firestore userPreferences/{uid} via useUserPreferences.
 *   2. Display & Interface (placeholder)
 *   3. Notification Preferences (placeholder)
 *   4. Client Preferences + Vendor Preferences (placeholder, side-by-side)
 *   5. Email Templates (placeholder, full-width)
 *
 * No Firebase Functions are used — the live section writes directly to
 * Firestore through the hook, which is gated by per-user security rules.
 *
 * @component
 */

import React, { useEffect, useState } from "react";
import {
  FaSlidersH,
  FaDesktop,
  FaBell,
  FaEnvelopeOpenText,
  FaSpinner,
  FaCheck,
  FaUndo,
  FaHardHat,
  FaProjectDiagram,
  FaUsers,
} from "react-icons/fa";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import useUserPreferences, {
  DEFAULT_PREFERENCES,
} from "../../hooks/useUserPreferences";
import "./AdminSettings.css";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Options for the "Default view" select in Project Preferences.
 * @constant {{value: string, label: string}[]}
 */
const VIEW_OPTIONS = [
  { value: "card", label: "Card view" },
  { value: "list", label: "List view" },
];

/**
 * Options for the "Default sort by" select in Project Preferences.
 * @constant {{value: string, label: string}[]}
 */
const SORT_OPTIONS = [
  { value: "startDate", label: "Start Date" },
  { value: "name", label: "Project Name" },
  { value: "status", label: "Project Status" },
  { value: "progress", label: "Project Progress" },
];

/**
 * Sort options for the Clients page. Keys must match the sort
 * keys accepted by AdminClients' sortKeyMap.
 * @constant {{value: string, label: string}[]}
 */
const CLIENT_SORT_OPTIONS = [
  { value: "clientName", label: "Client Name" },
  { value: "clientStatus", label: "Client Status" },
  { value: "projectName", label: "Project Name" },
  { value: "recordCreated", label: "Date Created" },
];

/**
 * Sort options for the Vendors page. Keys must match the sort
 * keys accepted by AdminVendors' sortKeyMap.
 * @constant {{value: string, label: string}[]}
 */
const VENDOR_SORT_OPTIONS = [
  { value: "vendorName", label: "Vendor Name" },
  { value: "category", label: "Category" },
  { value: "isClientFacing", label: "Client-Facing" },
  { value: "hasShowroom", label: "Showroom" },
  { value: "status", label: "Status" },
];

/**
 * Options for the "Sort direction" select in Project Preferences.
 * @constant {{value: string, label: string}[]}
 */
const DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascending (A → Z)" },
  { value: "desc", label: "Descending (Z → A)" },
];

/* ============================================
   SUB-COMPONENTS (module-level, not nested)
   ============================================ */

/**
 * Section card wrapper. Matches the look of AdminProfile's section cards.
 *
 * @param {Object} props
 * @param {string} props.title - Section heading
 * @param {React.ReactNode} props.icon - Icon shown before the title
 * @param {string} [props.subtitle] - Italic subtitle line
 * @param {boolean} [props.comingSoon=false] - Render a "Coming soon" pill
 * @param {string} [props.className] - Extra class name (e.g., for grid span)
 * @param {React.ReactNode} props.children - Section body
 * @returns {JSX.Element}
 */
const Section = ({
  title,
  icon,
  subtitle,
  comingSoon,
  className,
  children,
}) => (
  <div className={`as-section${className ? ` ${className}` : ""}`}>
    <div className="as-section-header">
      <div className="as-section-title">
        {icon && <span className="as-section-icon">{icon}</span>}
        <h2>{title}</h2>
        {comingSoon && <span className="as-coming-soon">Coming soon</span>}
      </div>
      {subtitle && <span className="as-section-subtitle">{subtitle}</span>}
    </div>
    <div className="as-section-content">{children}</div>
  </div>
);

/**
 * Single preference row — label + hint on the left, control on the right.
 *
 * @param {Object} props
 * @param {string} props.label - Main row label
 * @param {string} props.hint - Secondary hint text below the label
 * @param {React.ReactNode} props.control - Right-side control node
 * @returns {JSX.Element}
 */
const PrefRow = ({ label, hint, control }) => (
  <div className="as-pref-row">
    <div className="as-pref-label">
      <span className="as-pref-label-title">{label}</span>
      {hint && <span className="as-pref-label-hint">{hint}</span>}
    </div>
    <div className="as-pref-control">{control}</div>
  </div>
);

/**
 * Dashed chip used as a placeholder for future settings in the
 * "coming soon" cards. Purely visual — no interaction.
 *
 * @param {Object} props
 * @param {string} props.children - Chip label
 * @returns {JSX.Element}
 */
const PlaceholderChip = ({ children }) => (
  <span className="as-placeholder-chip">{children}</span>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * AdminSettings — Portal preferences and configuration page.
 *
 * @param {Object} props - Component props
 * @param {Object} [props.user] - Firebase auth user (injected by router)
 * @param {Object} [props.userData] - Legacy team data (unused here;
 *   preferences are loaded via useUserPreferences)
 * @returns {JSX.Element}
 */
function AdminSettings({ user, userData }) {
  const { preferences, updatePreferences, isLoading } = useUserPreferences();

  const [view, setView] = useState(preferences.projectsDefaultView);
  const [sortBy, setSortBy] = useState(preferences.projectsDefaultSort);
  const [direction, setDirection] = useState(preferences.projectsSortDirection);

  const [clientView, setClientView] = useState(preferences.clientsDefaultView);
  const [clientSortBy, setClientSortBy] = useState(
    preferences.clientsDefaultSort,
  );
  const [clientDirection, setClientDirection] = useState(
    preferences.clientsSortDirection,
  );

  const [vendorView, setVendorView] = useState(preferences.vendorsDefaultView);
  const [vendorSortBy, setVendorSortBy] = useState(
    preferences.vendorsDefaultSort,
  );
  const [vendorDirection, setVendorDirection] = useState(
    preferences.vendorsSortDirection,
  );
  const [vendorShowInactive, setVendorShowInactive] = useState(
    preferences.vendorsShowInactive,
  );

  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientJustSaved, setClientJustSaved] = useState(false);
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorJustSaved, setVendorJustSaved] = useState(false);

  // Sync local form state whenever the loaded preferences change
  // (initial load, auth change, external update from another tab).
  useEffect(() => {
    setView(preferences.projectsDefaultView);
    setSortBy(preferences.projectsDefaultSort);
    setDirection(preferences.projectsSortDirection);
  }, [
    preferences.projectsDefaultView,
    preferences.projectsDefaultSort,
    preferences.projectsSortDirection,
  ]);

  useEffect(() => {
    setClientView(preferences.clientsDefaultView);
    setClientSortBy(preferences.clientsDefaultSort);
    setClientDirection(preferences.clientsSortDirection);
  }, [
    preferences.clientsDefaultView,
    preferences.clientsDefaultSort,
    preferences.clientsSortDirection,
  ]);

  useEffect(() => {
    setVendorView(preferences.vendorsDefaultView);
    setVendorSortBy(preferences.vendorsDefaultSort);
    setVendorDirection(preferences.vendorsSortDirection);
    setVendorShowInactive(preferences.vendorsShowInactive);
  }, [
    preferences.vendorsDefaultView,
    preferences.vendorsDefaultSort,
    preferences.vendorsSortDirection,
    preferences.vendorsShowInactive,
  ]);

  const dirty =
    !isLoading &&
    (view !== preferences.projectsDefaultView ||
      sortBy !== preferences.projectsDefaultSort ||
      direction !== preferences.projectsSortDirection);

  const clientDirty =
    !isLoading &&
    (clientView !== preferences.clientsDefaultView ||
      clientSortBy !== preferences.clientsDefaultSort ||
      clientDirection !== preferences.clientsSortDirection);

  const vendorDirty =
    !isLoading &&
    (vendorView !== preferences.vendorsDefaultView ||
      vendorSortBy !== preferences.vendorsDefaultSort ||
      vendorDirection !== preferences.vendorsSortDirection ||
      vendorShowInactive !== preferences.vendorsShowInactive);

  const hasUnsavedChanges = dirty || clientDirty || vendorDirty;

  // Warn on tab close / refresh / external navigation when any section
  // has unsaved preference changes. The browser shows its own generic
  // confirmation dialog — returnValue must be set for legacy support.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    // Intercept in-app link clicks (sidebar, breadcrumbs, etc.) at the
    // capture phase so React Router never sees the event if the user
    // cancels. BrowserRouter has no useBlocker, so this is the path.
    const handleLinkClick = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest?.("a[href]");
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      const confirmed = window.confirm(
        "You have unsaved preference changes. Leave without saving?",
      );
      if (!confirmed) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [hasUnsavedChanges]);

  /**
   * Persist the three Project Preferences fields to Firestore and
   * briefly show a "Saved!" confirmation on the button.
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePreferences({
        projectsDefaultView: view,
        projectsDefaultSort: sortBy,
        projectsSortDirection: direction,
      });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Save failed:", err);
      alert(`Could not save preferences: ${err.message || String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset the Project Preferences fields back to the app-wide
   * defaults from `DEFAULT_PREFERENCES` and persist them to
   * Firestore (overwriting any existing values on the doc).
   * @returns {Promise<void>}
   */
  const handleReset = async () => {
    const confirmed = window.confirm(
      "Reset project preferences to default settings?",
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const defaults = {
        projectsDefaultView: DEFAULT_PREFERENCES.projectsDefaultView,
        projectsDefaultSort: DEFAULT_PREFERENCES.projectsDefaultSort,
        projectsSortDirection: DEFAULT_PREFERENCES.projectsSortDirection,
      };
      setView(defaults.projectsDefaultView);
      setSortBy(defaults.projectsDefaultSort);
      setDirection(defaults.projectsSortDirection);
      await updatePreferences(defaults);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Reset failed:", err);
      alert(`Could not reset preferences: ${err.message || String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /** Persist Client Preferences. */
  const handleClientSave = async () => {
    setClientSaving(true);
    try {
      await updatePreferences({
        clientsDefaultView: clientView,
        clientsDefaultSort: clientSortBy,
        clientsSortDirection: clientDirection,
      });
      setClientJustSaved(true);
      setTimeout(() => setClientJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Client save failed:", err);
      alert(`Could not save preferences: ${err.message || String(err)}`);
    } finally {
      setClientSaving(false);
    }
  };

  /** Reset Client Preferences to defaults. */
  const handleClientReset = async () => {
    const confirmed = window.confirm(
      "Reset client preferences to default settings?",
    );
    if (!confirmed) return;

    setClientSaving(true);
    try {
      const defaults = {
        clientsDefaultView: DEFAULT_PREFERENCES.clientsDefaultView,
        clientsDefaultSort: DEFAULT_PREFERENCES.clientsDefaultSort,
        clientsSortDirection: DEFAULT_PREFERENCES.clientsSortDirection,
      };
      setClientView(defaults.clientsDefaultView);
      setClientSortBy(defaults.clientsDefaultSort);
      setClientDirection(defaults.clientsSortDirection);
      await updatePreferences(defaults);
      setClientJustSaved(true);
      setTimeout(() => setClientJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Client reset failed:", err);
      alert(`Could not reset preferences: ${err.message || String(err)}`);
    } finally {
      setClientSaving(false);
    }
  };

  /** Persist Vendor Preferences. */
  const handleVendorSave = async () => {
    setVendorSaving(true);
    try {
      await updatePreferences({
        vendorsDefaultView: vendorView,
        vendorsDefaultSort: vendorSortBy,
        vendorsSortDirection: vendorDirection,
        vendorsShowInactive: vendorShowInactive,
      });
      setVendorJustSaved(true);
      setTimeout(() => setVendorJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Vendor save failed:", err);
      alert(`Could not save preferences: ${err.message || String(err)}`);
    } finally {
      setVendorSaving(false);
    }
  };

  /** Reset Vendor Preferences to defaults. */
  const handleVendorReset = async () => {
    const confirmed = window.confirm(
      "Reset vendor preferences to default settings?",
    );
    if (!confirmed) return;

    setVendorSaving(true);
    try {
      const defaults = {
        vendorsDefaultView: DEFAULT_PREFERENCES.vendorsDefaultView,
        vendorsDefaultSort: DEFAULT_PREFERENCES.vendorsDefaultSort,
        vendorsSortDirection: DEFAULT_PREFERENCES.vendorsSortDirection,
        vendorsShowInactive: DEFAULT_PREFERENCES.vendorsShowInactive,
      };
      setVendorView(defaults.vendorsDefaultView);
      setVendorSortBy(defaults.vendorsDefaultSort);
      setVendorDirection(defaults.vendorsSortDirection);
      setVendorShowInactive(defaults.vendorsShowInactive);
      await updatePreferences(defaults);
      setVendorJustSaved(true);
      setTimeout(() => setVendorJustSaved(false), 2000);
    } catch (err) {
      console.error("[AdminSettings] Vendor reset failed:", err);
      alert(`Could not reset preferences: ${err.message || String(err)}`);
    } finally {
      setVendorSaving(false);
    }
  };

  return (
    <div className="as-container">
      <AdminPageBanner
        title="Settings"
        subtitle="Portal preferences and configuration"
      >
        <div className="apb-icon">
          <FaSlidersH />
        </div>
      </AdminPageBanner>

      {/* =============== LIVE: Project/Client/Vendor Page Preferences =============== */}
      <div className="as-two-col">
        <Section
          title="Projects Page Preferences"
          icon={<FaProjectDiagram />}
          subtitle="Choose how the Projects page looks and sorts by default"
        >
          <PrefRow
            label="Default view"
            hint="The layout the Projects page opens with."
            control={
              <select
                className="as-select"
                value={view}
                onChange={(e) => setView(e.target.value)}
                disabled={isLoading || saving}
              >
                {VIEW_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <PrefRow
            label="Default sort by"
            hint="Which column the Projects list is sorted on."
            control={
              <select
                className="as-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                disabled={isLoading || saving}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <PrefRow
            label="Sort direction"
            hint="Whether the sort runs ascending or descending."
            control={
              <select
                className="as-select"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                disabled={isLoading || saving}
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <div className="as-section-actions">
            <button
              type="button"
              className="as-reset-btn"
              onClick={handleReset}
              disabled={isLoading || saving}
              data-tooltip="Reset to default settings"
            >
              <FaUndo /> Default Settings
            </button>
            <button
              type="button"
              className={`as-save-btn${justSaved ? " as-save-btn-success" : ""}`}
              onClick={handleSave}
              disabled={isLoading || saving || (!dirty && !justSaved)}
            >
              {saving ? (
                <>
                  <FaSpinner className="as-spin" /> Saving…
                </>
              ) : justSaved ? (
                <>
                  <FaCheck /> Saved!
                </>
              ) : (
                "Save preferences"
              )}
            </button>
          </div>
        </Section>

        <Section
          title="Clients Page Preferences"
          icon={<FaUsers />}
          subtitle="Choose how the Clients page looks and sorts by default"
        >
          <PrefRow
            label="Default view"
            hint="The layout the Clients page opens with."
            control={
              <select
                className="as-select"
                value={clientView}
                onChange={(e) => setClientView(e.target.value)}
                disabled={isLoading || clientSaving}
              >
                {VIEW_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <PrefRow
            label="Default sort by"
            hint="Which column the Clients list is sorted on."
            control={
              <select
                className="as-select"
                value={clientSortBy}
                onChange={(e) => setClientSortBy(e.target.value)}
                disabled={isLoading || clientSaving}
              >
                {CLIENT_SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <PrefRow
            label="Sort direction"
            hint="Whether the sort runs ascending or descending."
            control={
              <select
                className="as-select"
                value={clientDirection}
                onChange={(e) => setClientDirection(e.target.value)}
                disabled={isLoading || clientSaving}
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            }
          />

          <div className="as-section-actions">
            <button
              type="button"
              className="as-reset-btn"
              onClick={handleClientReset}
              disabled={isLoading || clientSaving}
              data-tooltip="Reset to default settings"
            >
              <FaUndo /> Default Settings
            </button>
            <button
              type="button"
              className={`as-save-btn${clientJustSaved ? " as-save-btn-success" : ""}`}
              onClick={handleClientSave}
              disabled={
                isLoading || clientSaving || (!clientDirty && !clientJustSaved)
              }
            >
              {clientSaving ? (
                <>
                  <FaSpinner className="as-spin" /> Saving…
                </>
              ) : clientJustSaved ? (
                <>
                  <FaCheck /> Saved!
                </>
              ) : (
                "Save preferences"
              )}
            </button>
          </div>
        </Section>
      </div>

      <Section
        title="Vendors Page Preferences"
        icon={<FaHardHat />}
        subtitle="Choose how the Vendors page looks and sorts by default"
      >
        <PrefRow
          label="Default view"
          hint="The layout the Vendors page opens with."
          control={
            <select
              className="as-select"
              value={vendorView}
              onChange={(e) => setVendorView(e.target.value)}
              disabled={isLoading || vendorSaving}
            >
              {VIEW_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          }
        />

        <PrefRow
          label="Default sort by"
          hint="Which column the Vendors list is sorted on."
          control={
            <select
              className="as-select"
              value={vendorSortBy}
              onChange={(e) => setVendorSortBy(e.target.value)}
              disabled={isLoading || vendorSaving}
            >
              {VENDOR_SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          }
        />

        <PrefRow
          label="Sort direction"
          hint="Whether the sort runs ascending or descending."
          control={
            <select
              className="as-select"
              value={vendorDirection}
              onChange={(e) => setVendorDirection(e.target.value)}
              disabled={isLoading || vendorSaving}
            >
              {DIRECTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          }
        />

        <PrefRow
          label="Show inactive by default"
          hint="Include inactive vendors when the page first opens."
          control={
            <label className="as-switch">
              <input
                type="checkbox"
                checked={vendorShowInactive}
                onChange={(e) => setVendorShowInactive(e.target.checked)}
                disabled={isLoading || vendorSaving}
                aria-label="Show inactive vendors by default"
              />
              <span className="as-switch-slider" />
            </label>
          }
        />

        <div className="as-section-actions">
          <button
            type="button"
            className="as-reset-btn"
            onClick={handleVendorReset}
            disabled={isLoading || vendorSaving}
            data-tooltip="Reset to default settings"
          >
            <FaUndo /> Default Settings
          </button>
          <button
            type="button"
            className={`as-save-btn${vendorJustSaved ? " as-save-btn-success" : ""}`}
            onClick={handleVendorSave}
            disabled={
              isLoading || vendorSaving || (!vendorDirty && !vendorJustSaved)
            }
          >
            {vendorSaving ? (
              <>
                <FaSpinner className="as-spin" /> Saving…
              </>
            ) : vendorJustSaved ? (
              <>
                <FaCheck /> Saved!
              </>
            ) : (
              "Save preferences"
            )}
          </button>
        </div>
      </Section>

      {/* =============== PLACEHOLDER: Display & Interface =============== */}
      <Section
        title="Display & Interface"
        icon={<FaDesktop />}
        subtitle="Theme and layout tweaks"
        comingSoon
      >
        <div className="as-placeholder-grid">
          <PlaceholderChip>Select color scheme</PlaceholderChip>
          <PlaceholderChip>Dashboard sections order</PlaceholderChip>
          <PlaceholderChip>Single Project Page sections order</PlaceholderChip>
          <PlaceholderChip>Date Format</PlaceholderChip>
        </div>
      </Section>

      {/* =============== PLACEHOLDER: Notification Preferences =============== */}
      <Section
        title="Notification Preferences"
        icon={<FaBell />}
        subtitle="Choose what you get pinged about, and where"
        comingSoon
      >
        <div className="as-placeholder-grid">
          <PlaceholderChip>Email digests</PlaceholderChip>
          <PlaceholderChip>In-portal toasts</PlaceholderChip>
          <PlaceholderChip>Mentions & replies</PlaceholderChip>
        </div>
      </Section>

      {/* =============== PLACEHOLDER: Email Templates (full-width) =============== */}
      <Section
        title="Email Templates"
        icon={<FaEnvelopeOpenText />}
        subtitle="Reusable templates for client outreach"
        comingSoon
      >
        <div className="as-placeholder-grid">
          <PlaceholderChip>Project kickoff</PlaceholderChip>
          <PlaceholderChip>Change-order approval request</PlaceholderChip>
          <PlaceholderChip>Selections deadline reminder</PlaceholderChip>
          <PlaceholderChip>Project close-out</PlaceholderChip>
        </div>
      </Section>
    </div>
  );
}

export default AdminSettings;
